import { HttpErrorResponse }        from '@angular/common/http';
import { NormalizerService }        from '@app/core/http/normalizer.service';
import {
	IBaseHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                   from '@app/core/interfaces/api.interface';
import {
	ENTITY_TYPE,
	IActionItem,
	IBaseEntityUi
}                                   from '@app/core/interfaces/entity.interface';
import { AppInjector }              from '@app/core/services/app-injector.service';
import { LoggerService }            from '@app/shared/services/logger.service';
import { ObservableTypeEnum }       from '@core/interfaces/common.interfaces';
import {
	IStoreConfig,
	IStoreProcessingConfig,
	StoreActionsEnum
}                                   from '@core/interfaces/store.interface';
import { DialogModalCommonService } from '@core/modals/dialog-modal-common/dialog-modal-common.service';
import { AuthorizationService }     from '@core/services/authorization/authorization.service';
import { RouterService }            from '@core/services/route.service';
import { TranslationService }       from '@core/services/translation.service';
import {
	EntityActionEventEnum,
	EntityActionTypeEnum
}                                   from '@models/entity.model';
import { Update }                   from '@ngrx/entity/src/models';
import {
	select,
	Store
}                                   from '@ngrx/store';
import { LIST_OPERATIONS_METADATA } from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	IActiveFilterData
}                                   from '@shared/list-operations/model/filter.model';
import { IPaging }                  from '@shared/list-operations/model/paging.model';
import { ListOperationsService }    from '@shared/list-operations/services/list-operations.service';
import { CurrentUserService }       from '@shared/services/current-user.service';
import { ToasterService }           from '@shared/services/toaster.service';
import { UiService }                from '@shared/services/ui.service';
import {
	ArrayUtils,
	StringUtils
}                                   from '@utils/helpers';
import { DateUtilsHelperService }   from '@utils/helpers/date-utils-helper.service';
import {
	forkJoin,
	Observable,
	of,
	Subject
}                                   from 'rxjs';
import {
	share,
	take
}                                   from 'rxjs/operators';


export abstract class AbstractBaseService {

	protected readonly authorizationService: AuthorizationService;

	protected readonly currentUserService: CurrentUserService;

	protected readonly dateUtilsHelperService: DateUtilsHelperService;

	protected readonly dialogCommonService: DialogModalCommonService;

	protected readonly listOperationsService: ListOperationsService;

	protected readonly logger: LoggerService;

	protected readonly normalizer: NormalizerService;

	protected readonly routerService: RouterService;

	protected readonly translationService: TranslationService;

	protected readonly toasterService: ToasterService;

	protected readonly uiService: UiService;

	protected constructor(
		protected readonly store$: Store,
		private readonly selector: any,
		private readonly action: any
	) {
		const injector = AppInjector.getInjector();
		this.authorizationService = injector.get( AuthorizationService );
		this.currentUserService = injector.get( CurrentUserService );
		this.dateUtilsHelperService = injector.get( DateUtilsHelperService );
		this.dialogCommonService = injector.get( DialogModalCommonService );
		this.logger = injector.get( LoggerService );
		this.listOperationsService = injector.get( ListOperationsService );
		this.normalizer = injector.get( NormalizerService );
		this.routerService = injector.get( RouterService );
		this.translationService = injector.get( TranslationService );
		this.toasterService = injector.get( ToasterService );
		this.uiService = injector.get( UiService );
	}

	get router() {
		return {
			helpers: this.routerService.helpers
		};
	}

	protected generateParams( params: IBaseHttpParams ): IBaseHttpParams {

		if ( !params ) { return null; }

		let pageNumber: number = LIST_OPERATIONS_METADATA?.default?.paging?.pageNumber ?? 20;
		let pageSize: number = LIST_OPERATIONS_METADATA?.default?.paging?.pageSize ?? 20;


		// pageSize
		if ( Number.isInteger( params?.paging?.pageSize ) ) { pageSize = params.paging?.pageSize; }

		// pageNumber
		if ( Number.isInteger( params?.paging?.pageNumber ) ) { pageNumber = params.paging?.pageNumber; }

		const param: IBaseHttpParams = {
			search: params?.search?.length ? params?.search : null,
			sort: params?.sort ? params?.sort : null,
			paging: { pageSize, pageNumber }
		};

		// filter
		if ( params.activeFilters ) {
			Object.entries( params.activeFilters )
			.forEach( ( [ filterIdentifier, activeFilterData ]: [ FilterIdentifiersEnum, IActiveFilterData ] ) => {
				param[ filterIdentifier ] = activeFilterData?.param;
			} );
		}

		return param;
	}

	protected handleMissingEntity<T>(
		action: RequestActionEnum,
		requestState: RequestState,
		status: number,
		asObservable?: true
	): Observable<RequestCompleteEntityState<T>>;
	protected handleMissingEntity<T>(
		action: RequestActionEnum,
		requestState: RequestState,
		status: number,
		asObservable?: false
	): RequestCompleteEntityState<T>;
	protected handleMissingEntity<T>(
		action: RequestActionEnum,
		requestState: RequestState,
		status: number,
		asObservable?: boolean
	): Observable<RequestCompleteEntityState<T>> | RequestCompleteEntityState<T> {
		const requestCompleteEntityState: RequestCompleteEntityState<T> = {
			entity: null,
			action,
			requestState,
			error: new HttpErrorResponse( { status } )
		};

		return asObservable
					 ? of( requestCompleteEntityState )
					 : requestCompleteEntityState;

	}

	protected mapEntityActions<T>(
		entity: T,
		entityActionsMap: Map<EntityActionEventEnum, IActionItem>
	): T {

		if ( !entity ) {
			return entity;
		}

		let ui: object;

		let entityActions: IBaseEntityUi;

		// tslint:disable-next-line:no-string-literal
		if ( entity[ 'ui' ] ) {
			// tslint:disable-next-line:no-string-literal
			ui = entity[ 'ui' ];
		}

		// tslint:disable-next-line:no-string-literal
		if ( ui && ui[ 'entityActions' ] ) {
			// tslint:disable-next-line:no-string-literal
			entityActions = ui[ 'entityActions' ];
		} else {
			entityActions = {};
		}

		if ( entityActionsMap ) {
			entityActionsMap.forEach( ( actionItem: IActionItem, entityActionEvent: EntityActionEventEnum ) => {
				if ( entityActionEvent.startsWith( EntityActionTypeEnum.ENTITY ) ) {
					const actionKey: string = StringUtils.toCamelCase( entityActionEvent.replace( EntityActionTypeEnum.ENTITY, '' ) );

					entityActions = Object.assign(
						{},
						{ ...entityActions, [ actionKey ]: actionItem }
					);
				}
			} );

			return { ...entity, ui: { ...ui, entityActions } };
		}
		return entity;
	}

	isUuidValid( uuid: string ): boolean {
		if ( uuid?.length !== 36 ) { return false; }
		const uuidRegexPattern: RegExp = new RegExp( '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}' );
		return !!uuid.match( uuidRegexPattern );
	}

	clearRoutes( caller = '' ): void {
		this.routerService.clearRoutes( caller );
	}

	// SHARED ENTITY SELECTORS ###########################################################################
	storeList$<T>(): Observable<T[]> {
		return this.store$.pipe( select<Record<string, T[]>, T[]>( this.selector.list ) );
	}

	storeListByIds$<T>( ids: string[] ): Observable<T[]> {
		return this.store$.pipe( select<Record<string, T[]>, T[]>( this.selector.listByIds( ids ) ) );
	}

	storeEntityById$<T>( entityId: string ): Observable<T> {
		return this.store$.pipe( select<Record<string, T>, T>( this.selector.read( entityId ) ) );
	}

	storeTotal$(): Observable<number> {
		return this.store$.pipe( select<Record<string, number>, number>( this.selector.adapterSelector.total ) );
	}


	// SHARED ENTITY SETTERS ###########################################################################
	// Entities
	storeAddMany<T>( entities: T[], caller?: string ): void {
		this.store$.dispatch( this.action.addMany( { entities, caller } ) );
	}

	storeSetAll<T>( entities: T[], caller?: string ): void {
		this.store$.dispatch( this.action.setAll( { entities, caller } ) );
	}

	storeUpsertMany<T>( entities: T[], caller: string ): void {
		this.logger.debug( caller += ' | AbstractBaseService:storeUpsertMany' );
		this.store$.dispatch( this.action.upsertMany( { entities, caller } ) );
	}

	storeUpdateMany<T>( entities: T[] ): void {
		const entitiesUpdateData: Update<T>[] = [];
		entities.forEach( entity => {
			entitiesUpdateData.push( {
				id: (
					entity as ENTITY_TYPE
				).id, changes: entity
			} );
		} );
		this.store$.dispatch( this.action.updateMany( { entitiesUpdateData } ) );
	}

	storeRemoveMany<T>( entities: T[] ): void {
		const entityIds: string[] = [];
		entities.forEach( entity => {
			entityIds.push( (
				entity as ENTITY_TYPE
			).id );
		} );
		this.store$.dispatch( this.action.removeMany( { entityIds } ) );
	}

	// Entity
	storeAddOne<T>( entity: T ): void {
		this.store$.dispatch( this.action.addOne( { entity } ) );
	}

	storeSetOne<T>( entity: T ): void {
		this.store$.dispatch( this.action.setOne( { entity } ) );
	}

	storeUpsertOne<T>( entity: T, caller?: string ): void {
		this.logger.debug( caller += ' | AbstractBaseService:storeUpsertOne' );
		this.store$.dispatch( this.action.upsertOne( { entity, caller } ) );
	}

	storeUpdateOne<T>( entity: T ): void {
		const entityUpdateData = {
			id: (
				entity as ENTITY_TYPE
			).id, changes: entity
		};
		this.store$.dispatch( this.action.updateOne( { entityUpdateData } ) );
	}

	storeRemoveOne<T>( entityId: string ): void {
		this.store$.dispatch( this.action.removeOne( { entityId } ) );
	}

	// Others
	storeRemoveAll<T>(): void {
		this.store$.dispatch( this.action.removeAll() );
	}

	// called by "requestThroughProxy" methods
	updateStore<T>( data: T | T[], storeAction: StoreActionsEnum, caller: string ): void {
		// fallback for response data (Entity | Entity[]) which doesn't match to the proper store adapter ( single | multiple )
		const dataIsArray = Array.isArray( data );
		const entities = Array.isArray( data ) ? data : [ data ];
		const entity = !Array.isArray( data ) ? data : data[ 0 ];

		switch ( storeAction ) {
			case StoreActionsEnum.ADD_MANY:
			case StoreActionsEnum.ADD_ONE:
				dataIsArray
				? this.storeAddMany( entities )
				: this.storeAddOne( entity );
				break;

			case StoreActionsEnum.SET_ALL:
			case StoreActionsEnum.SET_ONE:
				dataIsArray
				? this.storeSetAll( entities, caller )
				: this.storeSetOne( entity );
				break;

			case StoreActionsEnum.UPSERT_MANY:
			case StoreActionsEnum.UPSERT_ONE:
				dataIsArray
				? this.storeUpsertMany( entities, caller )
				: this.storeUpsertOne( entity, caller );
				break;

			case StoreActionsEnum.UPDATE_MANY:
			case StoreActionsEnum.UPDATE_ONE:
				dataIsArray
				? this.storeUpdateMany( entities )
				: this.storeUpdateOne( entity );
				break;

			case StoreActionsEnum.REMOVE_ALL:
				this.storeRemoveAll();
				break;
			case StoreActionsEnum.REMOVE_MANY:
			case StoreActionsEnum.REMOVE_ONE:
				dataIsArray
				? this.storeRemoveMany( entities )
				: this.storeRemoveOne( (
					entity as ENTITY_TYPE
				).id );
				break;
		}
	}

	// PROXY ###########################################################################
	createEntityStoreProcessingConfig<U>(
		storeConfig?: IStoreConfig,
		storeSelector?: Observable<U>,
		storeUpdateAction?: StoreActionsEnum
	): IStoreProcessingConfig<U> {
		const storeProcessingConfig: IStoreProcessingConfig<U> = {
			selector: storeConfig?.getFromStoreFirst && storeSelector ? storeSelector : null,
			// will update by default, when storeUpdateAction is set and storeConfig.updateStore isn't explicitly set to false
			updater: storeConfig?.updateStore !== false && storeUpdateAction ? storeUpdateAction : null,
			caller: storeConfig?.caller
		};

		return storeProcessingConfig;
	}

	createListStoreProcessingConfig<U>(
		storeConfig?: IStoreConfig,
		storeSelector?: Observable<U[]>,
		storeUpdateAction?: StoreActionsEnum
	): IStoreProcessingConfig<U[]> {
		const storeProcessingConfig: IStoreProcessingConfig<U[]> = {
			selector: storeConfig?.getFromStoreFirst && storeSelector ? storeSelector : null,
			// will update by default, when storeUpdateAction is set and storeConfig.updateStore isn't explicitly set to false
			updater: storeConfig?.updateStore && storeUpdateAction ? storeUpdateAction : null,
			caller: storeConfig?.caller
		};

		return storeProcessingConfig;
	}

	requestListThroughProxy<T>(
		apiReq$: Observable<RequestCompleteListState<T>>,
		storeProcessingConfig?: IStoreProcessingConfig<T[]>,
		observableReturnType: ObservableTypeEnum = ObservableTypeEnum.HOT
	): Observable<RequestCompleteListState<T>> {

		function requestApi( srv ): Observable<RequestCompleteListState<T>> {

			const subject = new Subject<RequestCompleteListState<T>>();

			apiReq$.pipe( take( 1 ) )
			.subscribe( ( state: RequestCompleteListState<T> ) => {
				if ( (
							 state.requestState === RequestState.SUCCESS || state.requestState === RequestState.NO_RESULT
						 ) &&
						 storeProcessingConfig?.updater
				) {
					srv?.updateStore( state.list, storeProcessingConfig?.updater, storeProcessingConfig?.caller );
				}
				subject.next( state );
				subject.complete();
				subject.unsubscribe();
			} );

			return subject.asObservable();
		}

		// if ( storeProcessingConfig?.selector ) {
		// 	const selector = storeProcessingConfig?.selector;
		// 	const req$ = selector
		// 		? selector
		// 		: this.storeList$<T>();
		//
		// 	return req$.pipe(
		// 		map( ( entities: T[] ) =>
		// 			this.normalizer.handleStoreListResponse<T>( entities )
		// 		),
		// 		switchMap( ( state: RequestCompleteListState<T> ) => {
		// 			return state.requestState === RequestState.NO_RESULT
		// 						 ? requestApi( this )
		// 						 : of( state );
		// 			return null;
		// 		} ),
		// 		take( 1 )
		// 	);
		// } else {
		// return requestApi( this );
		return observableReturnType === ObservableTypeEnum.COLD
					 ? apiReq$
					 : requestApi( this );
		// }
	}


	requestEntityThroughProxy<T>(
		entityId: string,
		apiReq$: Observable<RequestCompleteEntityState<T>>,
		storeProcessingConfig?: IStoreProcessingConfig<T>,
		// entity?: ENTITY_TYPE
		observableReturnType: ObservableTypeEnum = ObservableTypeEnum.HOT
	): Observable<RequestCompleteEntityState<T>> {

		function requestApi( srv ): Observable<RequestCompleteEntityState<T>> {

			const subject = new Subject<RequestCompleteEntityState<T>>();

			apiReq$.pipe( take( 1 ) )
			.subscribe( ( state: RequestCompleteEntityState<T> ) => {
				if ( state?.requestState === RequestState.SUCCESS && storeProcessingConfig?.updater ) {
					srv.updateStore( state.entity, storeProcessingConfig?.updater );
				}
				subject.next( state );
				subject.complete();
				subject.unsubscribe();
			} );

			return subject.asObservable();
		}

		// if ( storeProcessingConfig?.selector ) {
		// 	const selector = storeProcessingConfig?.selector;
		// 	const req$ = selector
		// 		? selector
		// 		: this.storeEntityById$<T>( entityId )
		//
		// 	return req$.pipe(
		// 		map( ( entity: T  ) =>
		// 			this.normalizer.handleStoreEntityResponse<T>( entity, RequestActionEnum.READ )
		// 		),
		// 		switchMap( ( state: RequestCompleteEntityState<T> ) => {
		// 			return state.requestState === RequestState.FAILED || state.requestState === RequestState.UNKNOWN
		// 						 ? requestApi( this )
		// 						 : of( state );
		// 		} ),
		// 		take( 1 )
		// 	);
		// } else {
		return observableReturnType === ObservableTypeEnum.COLD
					 ? apiReq$.pipe( share() )
					 : requestApi( this ).pipe( share() );
		// }
	}


	// HELPERS ###########################################################################
	/**
	 * Requests and combines data of all pages
	 *
	 * @param options.apiReqFn - a callbackFn which passes args from the args param to the apiRequest function
	 * @param options.requestOptions - an object with fields, needed for the apiRequest function
	 * @param options.fieldNameOfParamsData - the fieldName of the requestOptions, where the param property with paging data is
	 * @param options.requestPageSize - optionally define pageSize for requests, default is 20
	 *
	 * @return when all requests succeed the returned RequestCompleteListState<T> will contain combined data of all requests
	 *   when one request fails the failed RequestCompleteListState<T> will be returned
	 *
	 */
	requestAllPagesDataByOptionArg<T>(
		options: {
			apiReqFn: ( requestOptionsArg: object ) => Observable<RequestCompleteListState<T>>,
			requestOptions: object,
			fieldNameOfParamsData: string,
			requestPageSize?: number
		}
	): Observable<RequestCompleteListState<T>> {
		const {
			apiReqFn,
			requestOptions,
			fieldNameOfParamsData,
			requestPageSize
		} = options;


		const subject: Subject<RequestCompleteListState<T>> = new Subject<RequestCompleteListState<T>>();

		// subject next and complete helper
		function updateSubjectAndUnsubscribe( resultState: RequestCompleteListState<T> ): void {
			subject.next( resultState );
			subject.complete();
			subject.unsubscribe();
		}

		// generates api req observable for page
		function generateApiRequestForPage( pageNumber: number ): Observable<RequestCompleteListState<T>> {

			const paramsPagingOverwrite: IPaging = {
				pageSize: requestPageSize ?? 200,
				pageNumber
			};

			// update paging data
			requestOptions[ fieldNameOfParamsData ] = {
				...requestOptions[ fieldNameOfParamsData ],
				paging: paramsPagingOverwrite
			};

			// request with updated paging
			return apiReqFn( requestOptions );
		}

		const firstPageRequest$ = generateApiRequestForPage( 0 );
		let allPageRequestsResult: RequestCompleteListState<T>;

		// start first api request
		firstPageRequest$.pipe( take( 1 ) )
		.subscribe( ( firstRequestState: RequestCompleteListState<T> ) => {
			// checks result of first request and if further pages exists
			const requestWasSuccessful = firstRequestState.requestState === RequestState.SUCCESS;
			const pageCount = firstRequestState?.paging?.pageCount;
			const doesMorePagesExists = pageCount > 1;

			// assign result of first request to result return object
			allPageRequestsResult = { ...firstRequestState };

			// processes requests for other pages if exists
			if ( requestWasSuccessful && doesMorePagesExists ) {
				// creates api requests for other pages
				const otherPagesRequestList: Observable<RequestCompleteListState<T>>[] = [];
				for ( let i = 1; i < pageCount; i++ ) {
					otherPagesRequestList.push( generateApiRequestForPage( i ) );
				}

				// requests and process data of other pages
				forkJoin( ...otherPagesRequestList )
				.pipe( take( 1 ) )
				.subscribe( {
					// processes each page request
					next: ( nextRequestStatesList: RequestCompleteListState<T>[] ) => {

						// processes results of all page requests, if one request has failed further iteration will be aborted
						const allRequestsAreSuccessful = nextRequestStatesList.every( requestState => {
							// handles request results
							switch ( requestState.requestState ) {
								// updates allPageRequestsResult
								case RequestState.SUCCESS:
									allPageRequestsResult = {
										...allPageRequestsResult,
										list: [ ...allPageRequestsResult?.list, ...requestState?.list ]
									};
									return true;
								// returns failed request and aborts further processing
								case RequestState.FAILED:
									allPageRequestsResult = { ...requestState };
									return false;
							}
						} );

						// updates paging when all requests were successful
						// -> all data on one page, no further pages, pageSize is set to amount of listItems
						if ( allRequestsAreSuccessful ) {
							allPageRequestsResult = {
								...allPageRequestsResult,
								paging: {
									...allPageRequestsResult?.paging,
									pageNumber: 0,
									pageSize: allPageRequestsResult?.list?.length,
									pageCount: 0
								}
							};
						}
					},
					// returns final result
					complete: () => {
						updateSubjectAndUnsubscribe( allPageRequestsResult );
					}
				} );
			} else { // returns firstRequest result if request wasn't successful and no more pages exists
				updateSubjectAndUnsubscribe( allPageRequestsResult );
			}
		} );

		return subject.asObservable();
	}


	/**
	 * Requests and combines data of all pages
	 *
	 * @param apiReqFn - a callbackFn which passes args from the args param to the apiRequest function
	 * @param args - an array of arguments, needed for the apiRequest function -> the order of the args array needs to be exactly the same, as the needed args
	 * @param indexForParamsInArgsArray - the index of the args array param, where the param property with paging data is
	 * @param requestPageSize - optionally define pageSize for requests, default is 200
	 *
	 * @return when all requests succeed the returned RequestCompleteListState<T> will contain combined data of all requests
	 *   when one request fails the failed RequestCompleteListState<T> will be returned
	 */
	requestAllPagesData<T>(
		apiReqFn: ( ...args ) => Observable<RequestCompleteListState<T>>,
		args: any[],
		indexForParamsInArgsArray: number,
		requestPageSize?: number
	): Observable<RequestCompleteListState<T>> {

		const subject = new Subject<RequestCompleteListState<T>>();

		// subject next and complete helper
		function updateSubjectAndUnsubscribe( resultState: RequestCompleteListState<T> ) {
			subject.next( resultState );
			subject.complete();
			subject.unsubscribe();
		}

		// generates api req observable for page
		function generateApiRequestForPage( pageNumber: number ): Observable<RequestCompleteListState<T>> {

			const paramsPagingOverwrite: IPaging = {
				pageSize: requestPageSize ?? 200,
				pageNumber
			};

			// overwrites paging config to optimize request amounts
			if ( args[ indexForParamsInArgsArray ]?.paging ) {
				args[ indexForParamsInArgsArray ] = {
					...args[ indexForParamsInArgsArray ],
					paging: paramsPagingOverwrite
				};
			// inserts paging config as the only api request param (i.e. as 1st element of `args`) if no api request params exist yet
			} else {
				args = ArrayUtils.insertAtIndex(
					args,
					[ { paging: paramsPagingOverwrite } ],
					indexForParamsInArgsArray,
					{ appendOnOutOfBounds: true, removeFromSource: true }
				);
			}

			// if any args exists returns api request with args else without
			return args?.length
						 ? apiReqFn( args )
						 : apiReqFn();
		}

		const firstPageRequest$ = generateApiRequestForPage( 0 );
		let allPageRequestsResult: RequestCompleteListState<T>;

		// start first api request
		firstPageRequest$.pipe( take( 1 ) )
		.subscribe( ( firstRequestState: RequestCompleteListState<T> ) => {
			// checks result of first request and if further pages exist
			const requestWasSuccessful = firstRequestState.requestState === RequestState.SUCCESS;
			const pageCount = firstRequestState?.paging?.pageCount;
			const doesMorePagesExists = pageCount > 1;

			// assign result of first request to result return object
			allPageRequestsResult = { ...firstRequestState };

			// processes requests for other pages if exists
			if ( requestWasSuccessful && doesMorePagesExists ) {
				// creates api requests for other pages
				const otherPagesRequestList: Observable<RequestCompleteListState<T>>[] = [];
				for ( let i = 1; i < pageCount; i++ ) {
					otherPagesRequestList.push( generateApiRequestForPage( i ) );
				}

				// requests and process data of other pages
				forkJoin( ...otherPagesRequestList )
				.pipe( take( 1 ) )
				.subscribe( {
					// processes each page request
					next: ( nextRequestStatesList: RequestCompleteListState<T>[] ) => {

						// processes results of all page requests, if one request has failed further iteration will be aborted
						const allRequestsAreSuccessful = nextRequestStatesList.every( requestState => {
							// handles request results
							switch ( requestState.requestState ) {
								// updates allPageRequestsResult
								case RequestState.SUCCESS:
									allPageRequestsResult = {
										...allPageRequestsResult,
										list: [ ...allPageRequestsResult?.list, ...requestState?.list ]
									};
									return true;
								// returns failed request and aborts further processing
								case RequestState.FAILED:
									allPageRequestsResult = { ...requestState };
									return false;
							}
						} );

						// updates paging when all requests were successful
						// -> all data on one page, no further pages, pageSize is set to amount of listItems
						if ( allRequestsAreSuccessful ) {
							allPageRequestsResult = {
								...allPageRequestsResult,
								paging: {
									...allPageRequestsResult?.paging,
									pageNumber: 0,
									pageSize: allPageRequestsResult?.list?.length,
									pageCount: 0
								}
							};
						}
					},
					// returns final result
					complete: () => {
						updateSubjectAndUnsubscribe( allPageRequestsResult );
					}
				} );
			} else { // returns firstRequest result if request wasn't successful and no more pages exists
				updateSubjectAndUnsubscribe( allPageRequestsResult );
			}
		} );

		return subject.asObservable();
	}
}
